import { BigLoader } from "@circle-react-shared/BigLoader";
import { useUpgradePlanModal } from "@circle-react-shared/UpgradePlan";
import { useFetchPlan } from "@circle-react-shared/UpgradePlan/hooks/useFetchPlan";
import { Typography } from "@circle-react-uikit/Typography";
import { MINIMUM_REQUIRED_PLAN_TIER } from "@circle-react/components/SettingsApp/CommunityAi/constants";
import type { CompassModalProps } from "@circle-react/components/SettingsApp/CompassCompat/modalHelpers";
import { SanitizeHTMLString } from "@circle-react/components/shared/uikit/SanitizeContent";
import { useIsCommunityAiIncludedInCurrentPlan } from "@circle-react/hooks/useIsActivityScoreEnabled";
import { usePlanStatus } from "@circle-react/hooks/usePlanStatus";
import { Modal } from "@circleco/compass/components/Modal";
import { useNavigate } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";

import { ActivityScoresFeature } from "./ActivityScoresFeature";
import {
  ACTIVITY_SCORES_FEATURE,
  COPILOT_FEATURE,
  type FeatureType,
} from "./constants";
import { CopilotFeature } from "./CopilotFeature";
import { MarketingItem } from "./MarketingItem";

interface CommunityAiUpgradeModalComponentProps extends CompassModalProps {
  afterClose?: () => void;
  feature?: FeatureType;
  onSuccess?: () => void;
  source?: string;
}

export const CommunityAiUpgradeModalComponent = ({
  afterClose,
  feature = ACTIVITY_SCORES_FEATURE,
  source,
  onSuccess,
  open: isOpen,
  onOpenChange,
}: CommunityAiUpgradeModalComponentProps) => {
  const { changePlanModalParams } = usePlanStatus();
  const navigate = useNavigate();
  const upgradePlanModal = useUpgradePlanModal();
  const isCommunityAiIncludedInCurrentPlan =
    useIsCommunityAiIncludedInCurrentPlan();
  const planTierOrId = changePlanModalParams(MINIMUM_REQUIRED_PLAN_TIER);
  const { planTier, planId } = planTierOrId;

  const { plan, isPlanLoading } = useFetchPlan({
    planTier,
    planId,
  });

  const handleClose = () => {
    afterClose?.();
    onOpenChange(false);
  };

  const onChangePlanSuccess = () => {
    void navigate({ to: "/settings/general" });
    onSuccess?.();
    handleClose();
  };

  const handleClick = async () => {
    if (isCommunityAiIncludedInCurrentPlan) {
      void navigate({ to: "/settings/general" });
      handleClose();
    } else {
      await upgradePlanModal.show({
        planId: plan.id,
        onSuccess: onChangePlanSuccess,
        source,
        successButtonLabel: t(
          "settings.community_ai.marketing_modal.go_to_community_ai",
        ),
        showPlanDetails: true,
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        {isPlanLoading ? (
          <div className="my-28">
            <BigLoader />
          </div>
        ) : (
          <>
            <Modal.Header
              title={`${t("settings.community_ai.marketing_modal.title.unlock")} ${t("settings.community_ai.marketing_modal.title.community_ai")}`}
            />
            <Modal.Body>
              <Typography.BodyMd color="text-default">
                {t("settings.community_ai.marketing_modal.subtitle")}
              </Typography.BodyMd>

              <div className="mt-4">
                <div className="flex size-full pt-2">
                  <div className="max-w-xl">
                    {feature === COPILOT_FEATURE && <CopilotFeature />}
                    {feature === ACTIVITY_SCORES_FEATURE && (
                      <ActivityScoresFeature />
                    )}

                    <div className="bg-tertiary mt-6 rounded-sm p-4 pt-2">
                      <div className="mb-2">
                        <Typography.LabelXxs color="text-default">
                          {t("settings.community_ai.marketing_modal.plus")}
                        </Typography.LabelXxs>
                      </div>
                      {feature === COPILOT_FEATURE && (
                        <MarketingItem localeItem="activity_scores" />
                      )}
                      {feature === ACTIVITY_SCORES_FEATURE && (
                        <MarketingItem localeItem="copilot" />
                      )}
                      <MarketingItem localeItem="transcriptions" />
                    </div>
                  </div>
                </div>

                {!isCommunityAiIncludedInCurrentPlan && (
                  <div className="my-3 text-center">
                    <Typography.BodySm color="text-default">
                      <SanitizeHTMLString
                        content={t(
                          "settings.community_ai.marketing_modal.by_proceeding",
                          {
                            plan_name: plan.display_name,
                            price: plan.monthly_base_amount_with_interval,
                          },
                        )}
                        as="span"
                      />
                    </Typography.BodySm>
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer
              primaryAction={{
                label: t("settings.community_ai.marketing_modal.submit"),
                onClick: () => void handleClick(),
              }}
              layout="full-width"
            />
          </>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
